"use client";

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Bell } from "lucide-react";
import type { ShopNotificationRow } from "@/modules/notifications/domain/repo/list-shop-notifications";
import { formatDateTime } from "@/shared/lib/date-time";
import {
  useMarkAllShopNotificationsReadMutation,
  useMarkShopNotificationReadMutation,
  useShopNotificationSummaryQuery,
} from "../api/shop-notifications-queries";

const SEVERITY_DOT: Record<string, string> = {
  normal: "bg-primary",
  warning: "bg-amber-500",
  critical: "bg-destructive",
};

export function ShopNotificationBell() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const navigate = useNavigate();
  const { data, isError, isLoading } = useShopNotificationSummaryQuery(shopId);
  const markRead = useMarkShopNotificationReadMutation(shopId);
  const markAllRead = useMarkAllShopNotificationsReadMutation(shopId);

  if (!shopId) return null;

  const total = data?.totalUnread ?? 0;
  const badgeLabel = total > 99 ? "99+" : String(total);
  const hasUnread = total > 0;
  const items = data?.items ?? [];

  const handleOpen = (item: ShopNotificationRow) => {
    if (item.isUnread) {
      markRead.mutate(item.id);
    }
    void navigate({ to: item.href });
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative size-8"
          aria-label="ການແຈ້ງເຕືອນຮ້ານ"
        >
          <Bell className="size-4" aria-hidden />
          {hasUnread ? (
            <span
              className="absolute -top-0.5 -right-0.5 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-[#F26522] px-1 font-semibold text-[10px] text-white leading-none"
              aria-live="polite"
            >
              {badgeLabel}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[360px]" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-sm">ການແຈ້ງເຕືອນ</span>
            <div className="flex items-center gap-2">
              {hasUnread ? (
                <button
                  type="button"
                  className="text-secondary text-xs hover:underline disabled:opacity-50"
                  disabled={markAllRead.isPending}
                  onClick={() => markAllRead.mutate()}
                >
                  ອ່ານທັງໝົດ
                </button>
              ) : null}
              {isLoading ? (
                <span className="text-muted-foreground text-xs">...</span>
              ) : (
                <span className="text-muted-foreground text-xs">
                  {hasUnread ? `${total} ຍັງບໍ່ອ່ານ` : "ບໍ່ມີໃໝ່"}
                </span>
              )}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {isError ? (
          <p className="px-3 py-4 text-muted-foreground text-sm">
            ບໍ່ສາມາດໂຫຼດແຈ້ງເຕືອນ
          </p>
        ) : items.length === 0 ? (
          <p className="px-3 py-4 text-muted-foreground text-sm">
            ບໍ່ມີການແຈ້ງເຕືອນ
          </p>
        ) : (
          <div className="max-h-[min(420px,70vh)] overflow-y-auto">
            {items.map((item) => (
              <NotificationRow key={item.id} item={item} onOpen={handleOpen} />
            ))}
          </div>
        )}

        <div className="border-t px-3 py-2">
          <button
            type="button"
            className="w-full text-center text-secondary text-xs hover:underline"
            onClick={() =>
              void navigate({
                to: "/app/shops/$shopId/notifications",
                params: { shopId },
              })
            }
          >
            ເບິ່ງການແຈ້ງເຕືອນທັງໝົດ
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationRow({
  item,
  onOpen,
}: {
  item: ShopNotificationRow;
  onOpen: (item: ShopNotificationRow) => void;
}) {
  const isUnread = Boolean(item.isUnread);

  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-start gap-2 px-3 py-2.5 text-left transition hover:bg-muted/60",
        isUnread && "bg-muted/30",
      )}
      onClick={() => onOpen(item)}
    >
      <span
        className={cn(
          "mt-1.5 size-2 shrink-0 rounded-full",
          SEVERITY_DOT[item.severity] ?? SEVERITY_DOT.normal,
          isUnread && "ring-2 ring-[#F26522]/40 ring-offset-1",
        )}
        aria-hidden
      />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block text-sm leading-snug",
            isUnread ? "font-semibold" : "font-medium",
          )}
        >
          {item.title}
        </span>
        {item.body ? (
          <span className="block truncate text-muted-foreground text-xs">
            {item.body}
          </span>
        ) : null}
        <span className="mt-0.5 block text-muted-foreground text-[11px]">
          {formatDateTime(item.createdAt)}
        </span>
      </span>
    </button>
  );
}
